import { PageSkeleton } from "../../components/brand/Experience";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Printer, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API, api, fmtDateTime, MODE_FR, SIDE_FR } from "../../lib/api";
import { missionName, useCatalog } from "../../hooks/useData";
import { Logo } from "../../components/brand/Logo";
import { Delta, Panel, SimBadge } from "./CareLayout";
import { LEVEL_FR, SavannaScores, SavannaSides } from "./SavannaCard";

const CMP = [
  ["rom_shoulder_flexion_max", "Flexion d'épaule max (côté atteint)", "°"], ["rom_shoulder_abduction_max", "Abduction d'épaule max", "°"],
  ["functional_reach_max_cm", "Atteinte fonctionnelle max", " cm"], ["accuracy_mean", "Précision du mouvement", " %"],
  ["reaction_time_mean_ms", "Temps de réaction moyen", " ms", true], ["repetitions", "Répétitions", ""],
  ["affected_side_use_pct", "Utilisation du côté atteint", " %"], ["symmetry_pct", "Symétrie gauche/droite", " %"],
];

export default function SessionReport() {
  const { sid } = useParams();
  const { data: catalog } = useCatalog();
  const { data, refetch } = useQuery({ queryKey: ["report", sid], queryFn: () => api.get(`/sessions/${sid}/report`).then((r) => r.data), staleTime: 0 });
  const { data: attempts } = useQuery({ queryKey: ["attempts", sid], queryFn: () => api.get(`/sessions/${sid}/attempts`).then((r) => r.data) });
  const [notes, setNotes] = useState("");
  const [ai, setAi] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  useEffect(() => { if (data) { setNotes(data.report.therapist_notes); setAi(data.report.ai_summary || ""); } }, [data]);
  if (!data) return <PageSkeleton />;
  const { report: r, session: s, patient: p } = data;
  const m = r.metrics, first = data.first_session_metrics;

  const genAi = async () => {
    setAiBusy(true); setAi("");
    const res = await fetch(`${API}/sessions/${sid}/report/ai-summary`, { method: "POST" });
    const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const parts = buf.split("\n\n"); buf = parts.pop();
      parts.forEach((line) => {
        const ev = JSON.parse(line.replace(/^data: /, ""));
        if (ev.delta) setAi((o) => o + ev.delta);
        if (ev.error) toast.error("Synthèse IA indisponible");
      });
    }
    setAiBusy(false);
  };
  const saveNotes = async () => { await api.put(`/sessions/${sid}/report/notes`, { therapist_notes: notes }); toast.success("Notes enregistrées"); refetch(); };

  return (
    <div className="mx-auto max-w-5xl" data-testid="session-report">
      <div className="no-print mb-4 flex flex-wrap justify-between gap-2">
        <Link to={`/care/progress?patient=${p.id}`} data-testid="report-to-progress" className="flex h-11 items-center rounded-xl bg-brand px-4 text-sm font-bold text-white">Voir le graphique de progrès</Link>
        <button data-testid="print-report-button" onClick={() => window.print()} className="flex h-11 items-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-bold"><Printer className="h-4 w-4" />Imprimer / PDF</button>
      </div>
      <div className="rounded-3xl border border-border bg-card p-6 sm:p-10">
        <header className="flex flex-col justify-between gap-4 border-b border-border pb-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3"><Logo size={48} /><div><p className="font-display text-2xl font-extrabold">BAOBAB CARE</p><p className="text-xs font-bold tracking-[0.3em] text-primary-strong">RAPPORT DE SÉANCE</p></div></div>
          <SimBadge />
        </header>
        <div className="grid gap-4 py-6 text-sm sm:grid-cols-4">
          <p><span className="block text-xs text-muted-foreground/80">Patient</span><b data-testid="report-patient">{p.name}</b> · {p.age} ans</p>
          <p><span className="block text-xs text-muted-foreground/80">Date</span><b>{fmtDateTime(s.started_at)}</b></p>
          <p><span className="block text-xs text-muted-foreground/80">Durée</span><b data-testid="report-duration">{Math.floor(m.duration_s / 60)} min {m.duration_s % 60} s</b></p>
          <p><span className="block text-xs text-muted-foreground/80">Mode · Côté atteint</span><b>{MODE_FR[m.mode]} · {SIDE_FR[p.clinical_profile.affected_side]}</b></p>
        </div>
        {r.alerts.length > 0 && (
          <div className="mb-6 rounded-2xl bg-amber-50 p-4" data-testid="report-alerts">
            {r.alerts.map((a) => <p key={a} className="flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" />{a}</p>)}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="report-kpis">
          {[["Répétitions", `${m.successful_repetitions}/${m.repetitions}`], ["Utilisation côté atteint", `${m.affected_side_use_pct} %`], ["Amplitude flexion max", `${m.rom_shoulder_flexion_max ?? "—"}°`],
            ["Précision", `${m.accuracy_mean ?? "—"} %`], ["Temps de réaction", `${m.reaction_time_mean_ms ?? "—"} ms`], ["Atteinte max", `${m.functional_reach_max_cm ?? "—"} cm`],
            ["Douleur avant → après", `${m.pain_before ?? "—"} → ${m.pain_after ?? "—"}`], ["Fatigue avant → après", `${m.fatigue_before ?? "—"} → ${m.fatigue_after ?? "—"}`]].map(([k, v]) => (
            <div key={k} className="rounded-2xl bg-muted p-4"><p className="text-xs text-muted-foreground">{k}</p><p className="mt-1 font-display text-xl font-extrabold">{v}</p></div>
          ))}
        </div>
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Panel title="Exercices réalisés" testId="report-exercises">
            {m.exercises_completed.map((e) => (
              <div key={e.mission_key} className="flex justify-between border-b border-border/60 py-2 text-sm">
                <span>{missionName(catalog, e.mission_key)}</span><span className="font-mono">{e.success}/{e.repetitions} · {e.accuracy}%</span>
              </div>
            ))}
            <p className="mt-2 text-xs text-muted-foreground">Complétion : {m.completion_pct} % · Symétrie G/D : {m.symmetry_pct} % ({m.left_uses} G / {m.right_uses} D)</p>
          </Panel>
          <Panel title="Comparaison" testId="report-comparison">
            <div className="overflow-x-auto"><table className="w-full min-w-[480px] text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground/80"><th className="pb-2">Indicateur</th><th>1re séance</th><th>Précédente</th><th>Actuelle</th><th>Δ</th></tr></thead>
              <tbody>
                {CMP.map(([k, l, u, inv]) => (
                  <tr key={k} className="border-t border-border/60"><td className="py-2 pr-2">{l}</td><td className="font-mono">{first?.[k] ?? "—"}</td>
                    <td className="font-mono">{r.comparison[k]?.previous ?? "—"}</td><td className="font-mono font-bold">{m[k] ?? "—"}{u}</td><td><Delta v={r.comparison[k]?.delta} inverse={inv} /></td></tr>
                ))}
              </tbody>
            </table></div>
          </Panel>
        </div>
        {data.savanna?.map((run, i) => (
          <Panel key={run.id} title={`${LEVEL_FR[run.level] || "Niveau Savane"} · ${run.level === "forest" ? "membres supérieurs · tronc" : "membres inférieurs"}${data.savanna.length > 1 ? ` (partie ${i + 1})` : ""}`} className="mt-6" testId="report-savanna">
            <p className="mb-3 text-xs text-muted-foreground">Difficulté {run.difficulty} · {Math.round(run.affected_share * 100)} % des pas sur la jambe atteinte · {run.steps} pas réussis · précision {run.precision} % · temps de réaction {run.reaction_s} s · équilibre tenu {run.balance_s} s</p>
            <SavannaScores run={run} testId={`report-savanna-scores-${i}`} />
            <div className="mt-4"><SavannaSides run={run} /></div>
          </Panel>
        ))}
        <Panel title="Synthèse IA (non diagnostique)" className="mt-6" testId="report-ai-panel"
          right={<button data-testid="generate-ai-report-button" disabled={aiBusy} onClick={genAi} className="no-print flex h-10 items-center gap-2 rounded-xl bg-[#0A2540] px-3 text-xs font-bold text-white disabled:opacity-50"><Sparkles className="h-4 w-4" />{aiBusy ? "Rédaction…" : ai ? "Régénérer" : "Générer"}</button>}>
          <p data-testid="ai-summary-text" className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/85">{ai || "Cliquez sur « Générer » pour une synthèse rédigée automatiquement à partir des données de la séance."}</p>
        </Panel>
        <Panel title="Notes du thérapeute" className="mt-6">
          <textarea data-testid="therapist-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observations cliniques, consignes…"
            className="h-28 w-full rounded-xl border border-border p-3 text-sm focus:border-[#17A2A2] focus:outline-none" />
          <button data-testid="save-notes-button" onClick={saveNotes} className="no-print mt-2 h-11 rounded-xl bg-brand px-4 text-sm font-bold text-white">Enregistrer les notes</button>
        </Panel>
        <details className="mt-6" data-testid="attempts-log">
          <summary className="cursor-pointer text-sm font-bold text-foreground/75">Journal des interactions ({attempts?.length || 0})</summary>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[820px] text-xs">
              <thead><tr className="text-left text-muted-foreground/80"><th className="py-1">Mission</th><th>Mouvement</th><th>Côté</th><th>Attendu</th><th>Atteint (simulé)</th><th>Limite</th><th>Précision</th><th>TR (ms)</th><th>TM (ms)</th><th>Essais</th><th>Succès</th></tr></thead>
              <tbody>
                {attempts?.map((a) => (
                  <tr key={a.id} className="border-t border-border/60 font-mono"><td className="py-1 font-sans">{missionName(catalog, a.mission_key)} #{a.repetition}</td><td className="font-sans">{a.movement}</td><td>{SIDE_FR[a.side]}</td>
                    <td>{a.expected_amplitude}{a.unit}</td><td>{a.achieved_amplitude}{a.unit}</td><td>{a.safe_limit}{a.unit}</td><td>{a.accuracy}%</td><td>{a.reaction_time_ms}</td><td>{a.movement_time_ms}</td><td>{a.tries}</td><td>{a.success ? "✓" : "✗"}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
        <p className="mt-8 text-[11px] text-muted-foreground/80">Baobab Adventures est un outil d'aide à la rééducation et ne remplace pas l'évaluation ou le traitement par un professionnel de santé. Mesures issues d'une saisie tactile simulée — non assimilables à une capture clinique du mouvement.</p>
      </div>
    </div>
  );
}
